import type { JSX } from "solid-js";
import { createMemo, createUniqueId, getOwner, runWithOwner, Show } from "solid-js";
import { isNil } from "lodash-es";
import { Dynamic } from "solid-js/web";
import { IconButton } from "../button/Button";
import { useTranslation } from "../../state/i18n";
import { useFormServices } from "../../services/formServices";
import { useFormEditorSidePanel } from "../../pages/formEditor/FormEditorContent";
import { toField } from "../../fields/fields";
import { getIconCategoryColor } from "../../utils/iconColorUtil";
import SeparatorLine from "../SeparatorLine";
import { DynamicValueIconTile } from "../icon/IconTile";
import type { BaseField, FieldConfig } from "../../fields/types";
import type { IconName } from "../icon/Icon";
import type { FormScope } from "../../state/formVersion";
import { Scope } from "../../types";
import { TextInput } from "../inputs/TextInput";
import FormService from "../../services/FormService";
import TextAreaInput from "../inputs/TextAreaInput";
import { MAX_CHARACTERS_MD, MAX_CHARACTERS_SM } from "../../constants";
import { Form } from "../form/Form";
import { createForm } from "../form/createForm";
import { object } from "valibot";
import { optionalTextValue, requireText } from "../../utils/validation/base";

export const getPanelCloseButtonId = (scope: FormScope) =>
  scope === Scope.ROOT ? "panel-close-button" : "subform-panel-close-button";

export const getPanelScrollContainerId = (scope: FormScope) =>
  scope === Scope.ROOT ? "panel-scrollContainer" : "subform-panel-scrollContainer";

export type SidePanelSelection =
  | { type: "field"; field: BaseField<unknown, unknown>; fieldConfig: FieldConfig }
  | { type: "formDetails" };

type SidePanelProps = {
  header: {
    title: string;
    icon?: {
      name: IconName;
      color: string;
    };
    onClose: () => void;
  };
  children: JSX.Element;
};

const SidePanel = (props: SidePanelProps) => {
  const { t } = useTranslation();
  const { currentScope } = useFormServices();
  const headerId = `panel-header-${createUniqueId()}`;

  return (
    <aside
      aria-labelledby={headerId}
      class="flex size-full w-92 shrink-0 grow-0 flex-col overflow-x-hidden border-l border-subtle bg-surface-default pt-4"
    >
      <div class="flex flex-col gap-y-3">
        <div class="flex items-center justify-between gap-x-2 px-4">
          <div class="flex items-center gap-x-2">
            <Show when={props.header.icon}>
              {(icon) => (
                <DynamicValueIconTile icon={icon().name} iconClassList={{ [`${icon().color}`]: true }} size="md" />
              )}
            </Show>
            <h2 id={headerId} class="text-medium-semibold text-primary">
              {props.header.title}
            </h2>
          </div>
          <IconButton
            aria-label={t(($) => $.CLOSE_ARIA)}
            variant="tertiary"
            icon="x"
            onClick={props.header.onClose}
            class="ml-auto"
            data-id={getPanelCloseButtonId(currentScope)}
          />
        </div>
        <SeparatorLine orientation="horizontal" />
      </div>
      <div id={getPanelScrollContainerId(currentScope)} class="flex h-full flex-col overflow-y-auto px-4 pt-6 pb-11">
        {props.children}
      </div>
    </aside>
  );
};

type FieldEditorSidePanelProps = {
  field: BaseField<unknown, unknown>;
  fieldConfig: FieldConfig;
};

export const FieldEditorSidePanel = (props: FieldEditorSidePanelProps) => {
  const { setSelectedPanel } = useFormEditorSidePanel();
  const { fieldService, validationService } = useFormServices();
  const { t } = useTranslation();

  const field = createMemo(() => toField(props.fieldConfig.type));
  const issues = createMemo(() => validationService.getIssuesForField(props.fieldConfig.uid));

  const handleClose = () => {
    const elementToFocus = document.getElementById(props.fieldConfig.uid.toString())?.querySelector("button");
    setSelectedPanel(null);
    requestAnimationFrame(() => elementToFocus?.focus({ preventScroll: true }));
  };

  return (
    <SidePanel
      header={{
        title: t(props.field.titleKey),
        icon: { name: props.field.icon, color: getIconCategoryColor(props.field.category) },
        onClose: handleClose,
      }}
    >
      <Dynamic
        component={props.field.EditItem}
        uid={props.fieldConfig.uid}
        data={props.fieldConfig}
        setValue={(property, value) => fieldService.setFieldProperty(props.fieldConfig.uid, property as string, value)}
        getError={(property) =>
          issues().find((issue) => (issue.path ?? []).some((path) => path.key === property))?.message
        }
        getNestedErrors={(property) =>
          issues().filter((error) => (error.path ?? []).some((path) => "key" in path && path.key === property))
        }
        getGlobalErrors={() => issues().filter((error) => isNil(error.path))}
        propertyKeys={field().propertyKeys}
      />
    </SidePanel>
  );
};

export const FormDetailsSidePanel = () => {
  const { t } = useTranslation();
  const { setSelectedPanel } = useFormEditorSidePanel();
  const owner = getOwner();
  const form = FormService.getForm();

  const FormMetaSchema = object({
    name: requireText(MAX_CHARACTERS_SM),
    description: optionalTextValue(MAX_CHARACTERS_MD),
  });
  const formState = createForm(
    FormMetaSchema,
    (values) => {
      runWithOwner(owner, () => {
        FormService.setFormName(values.name);
        FormService.setFormDescription(values.description ?? "");
      });
    },
    {
      name: form.meta.name,
      description: !isNil(form.meta.description) ? form.meta.description : "",
    },
  );

  const handleClose = () => {
    const elementToFocus = document.getElementById("form-details");
    setSelectedPanel(null);
    requestAnimationFrame(() => elementToFocus?.focus({ preventScroll: true }));
  };

  return (
    <SidePanel header={{ title: t(($) => $.FORM_EDITOR_FORM_DETAILS_PANEL_TITLE), onClose: handleClose }}>
      <Form form={formState}>
        {({ value, error, updateValue }) => (
          <div class="flex flex-col gap-y-6">
            <TextInput
              required
              label={t(($) => $.FORM_EDITOR_FORM_DETAILS_PANEL_INPUT_NAME_LABEL)}
              value={value("name")}
              onBlur={(e) => {
                updateValue("name", e.target.value);
                formState.submit();
              }}
              errorMessage={error("name")}
            />
            <TextAreaInput
              maxLength={MAX_CHARACTERS_MD}
              label={t(($) => $.FORM_EDITOR_FORM_DETAILS_PANEL_INPUT_DESCRIPTION_LABEL)}
              value={value("description")}
              onBlur={(e) => {
                updateValue("description", e.target.value);
                formState.submit();
              }}
              errorMessage={error("description")}
            />
          </div>
        )}
      </Form>
    </SidePanel>
  );
};
